/**
 * Storage Account Detail Page
 *
 * Detailed view for a single ARM-discovered storage account.
 * Read-only — ARM accounts cannot be edited or deleted through this UI.
 *
 * Conformed to the Spec 197 design blueprint — migrated the bespoke
 * `min-h-screen bg-gray-50` shell / raw-gray header / hand-rolled
 * `bg-white … rounded … shadow` cards / raw `<table>` onto the canonical
 * Tailwind+token UI kit (DetailLayout / Card / StatCard / DescriptionList
 * / Button / DualScrollTable). Presentation-only; no data/logic change.
 *
 * Data sources:
 *   GET /api/azure/storage-accounts/:id          → AzureStorageAccountDetail
 *   GET /api/azure/storage-accounts/:id/containers → AzureStorageContainersResponse
 *
 * @feature storage-accounts-sme
 * @constitution v1.19.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMatch } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePageData } from '../../../hooks/usePageData';
import { formatBytes as fmtBytesCanonical } from '@/utils/formatBytes';
import { Database } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { apiClient } from '../../../services/apiClient';
import { useCanViewCost } from '../../../hooks/useCanViewCost';
import { NoAccessBadge } from '../../../components/collection/WorkerVisibility';
import { useHasAccess } from '../../../hooks/useAccess';
import { DualScrollTable, usePagination, PaginationControls } from '../../../components/common/TableControls';
import {
  Card,
  Badge,
  DescriptionList,
  DetailLayout,
  ErrorState,
  EmptyState,
  LoadingState,
  NotCollected,
  Extended,
  QuickGlance,
  chartTooltipStyle,
  CostSourceBadge,
  formatUsd,
} from '../../../components/ui';
import type {
  AzureStorageAccountDetail,
  AzureStorageContainer,
  AzureStorageContainersResponse,
} from '@shared/types/azureStorageAccounts';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';

// ============================================================================
// Helpers
// ============================================================================

/**
 * 🛑 Was a local DECIMAL formatter (÷1e9, ÷1e6, ÷1000) over byte counts built with 1024³.
 * Delegates to the canonical Spec 157 util so the detail page and the list agree — they
 * previously differed by 10% at TB scale for the same account.
 */
function formatBytes(bytes: number | null | string): string {
  const n = bytes === null ? 0 : Number(bytes);
  if (!n || !isFinite(n)) return '0 B';
  return fmtBytesCanonical(n);
}

function isGeoRedundant(replicationType: string | null): boolean {
  if (!replicationType) return false;
  const upper = replicationType.toUpperCase();
  return upper.includes('GRS') || upper.includes('RA-GRS') || upper.includes('RAGRS');
}

// ============================================================================
// Storage Account Detail Page Component
// ============================================================================

export const StorageAccountDetailPage: React.FC = () => {
  // The account id is the full ARM resource path (contains slashes).
  // Route is /storage-accounts/* so we reconstruct from the wildcard.
  const match = useMatch('/storage-accounts/*');
  // id is the unified-entity GUID (single segment) — no leading-slash reconstruction.
  const id = match?.params['*'] || undefined;
  // Owner directive: cost / spend figures are Admin + Manager only.
  const canViewCost = useCanViewCost();
  // Spec 285: the Backup-bound/Fleet "Protection" field is backup information — backup-licensed only.
  const canSeeBackup = useHasAccess('backup');

  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'containers'>('overview');

  // Container state
  const [containers, setContainers] = useState<AzureStorageContainer[]>([]);
  const [containersLoading, setContainersLoading] = useState(false);
  const [containersError, setContainersError] = useState<string | null>(null);

  // Pagination
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination(containers);

  // Fetch account data. Spec 226: through usePageData, so a refresh keeps the account on screen and a
  // failed one shows the Refresh badge; the error view below is for an account that never loaded.
  const query = useQuery({
    queryKey: ['azure-storage-account', id],
    // id is the full ARM path starting with '/subscriptions/...'
    // API base already ends without slash so no double-slash issue
    queryFn: async () =>
      (await apiClient.get<AzureStorageAccountDetail>(`/api/azure/storage-accounts/${id}`)).data ?? null,
    enabled: !!id,
  });
  const { data: account, isLoading: loading, error: loadError, refresh, isRefreshing, lastFetchedAt } = usePageData(query);
  const error = account ? null : loadError
    ? (loadError instanceof Error ? loadError.message : 'Failed to load storage account')
    : null;

  // Fetch containers
  const fetchContainers = useCallback(async () => {
    if (!id) return;

    setContainersLoading(true);
    setContainersError(null);

    try {
      const response = await apiClient.get<AzureStorageContainersResponse>(
        `/api/azure/storage-accounts/${id}/containers`
      );
      setContainers(response.data?.containers ?? []);
    } catch (err) {
      setContainersError(
        err instanceof Error ? err.message : 'Failed to load containers'
      );
    } finally {
      setContainersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'containers') {
      fetchContainers();
    }
  }, [activeTab, fetchContainers]);

  // The header's standard Refresh: the account, and the containers while their tab is open.
  const refreshPage = useCallback(async () => {
    await Promise.all([refresh(), activeTab === 'containers' ? fetchContainers() : Promise.resolve()]);
  }, [refresh, activeTab, fetchContainers]);

  // Loading state
  if (loading) {
    return (
      <DetailLayout title='Storage Account' backTo='/storage-accounts' backLabel='Storage Accounts'>
        <Card>
          <LoadingState rows={6} />
        </Card>
      </DetailLayout>
    );
  }

  // Error state
  if (error || !account) {
    return (
      <DetailLayout title='Storage Account' backTo='/storage-accounts' backLabel='Storage Accounts'>
        <ErrorState
          title='Error'
          message={error ?? 'Storage account not found'}
          onRetry={refresh}
        />
      </DetailLayout>
    );
  }

  const geo = account.isGeoRedundant ?? isGeoRedundant(account.replicationType);
  // Spec 259 (FR-001/FR-004): REAL trailing-30d Azure billing (dbo.azure_cost_daily); no $0.02/GB fallback.
  const actualCostPerMonth = account.actualMonthlyCostUsd;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    {
      key: 'containers',
      label: 'Containers',
      count: account.containerCount != null ? account.containerCount : undefined,
    },
  ];

  return (
    <DetailLayout
      helpId='auto.storage-account-detail'
      onRefresh={refreshPage}
      isRefreshing={isRefreshing || containersLoading}
      lastFetchedAt={lastFetchedAt}
      refreshError={loadError}
      title={account.accountName}
      subtitle={account.subscriptionName}
      icon={<Database className='h-7 w-7' />}
      backTo='/storage-accounts'
      backLabel='Storage Accounts'
      badges={
        <>
          {account.replicationType && (
            <span
              className={`px-2 py-1 text-xs font-medium rounded ${
                geo
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
              }`}
            >
              {account.replicationType}
              {geo && ' 🌐'}
            </span>
          )}
          {account.accessTier && (
            <span className='px-2 py-1 text-xs font-medium bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] rounded'>
              {account.accessTier} tier
            </span>
          )}
          <span className='px-2 py-1 text-xs font-medium bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] rounded'>
            ARM-discovered (read-only)
          </span>
          {account.noAccessSince && <NoAccessBadge since={account.noAccessSince} />}
        </>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={(key) => setActiveTab(key as 'overview' | 'containers')}
    >
      {/* ---------------------------------------------------------------- */}
      {/* Overview Tab                                                      */}
      {/* ---------------------------------------------------------------- */}
      {activeTab === 'overview' && (
        <div className='grid grid-cols-1 lg:grid-cols-4 gap-6'>
          {/* Storage Size Card */}
          <Card padding='lg'>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-4'>Used Storage</h3>
            <div className='text-center'>
              <div className='text-4xl font-bold text-[var(--color-accent)]'>
                {formatBytes(account.totalSizeBytes)}
              </div>
              {account.totalSizeBytes != null && (
                <p className='mt-2 text-sm text-[var(--color-text-secondary)]'>
                  {Number(account.totalSizeBytes).toLocaleString()} bytes
                </p>
              )}
              {account.accessTier && (
                <div className='mt-4 pt-4 border-t border-[var(--color-border)]'>
                  <div className='flex justify-between text-sm'>
                    <span className='text-[var(--color-text-secondary)]'>Access Tier:</span>
                    <span className='font-medium text-[var(--color-text-primary)]'>{account.accessTier}</span>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Actual Cost Card (Spec 259 — real Azure billing, trailing 30 days) — Admin/Manager only */}
          {canViewCost && (
            <Card padding='lg'>
              <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-4'>Monthly Cost</h3>
              <div className='text-center'>
                {actualCostPerMonth != null ? (
                  <>
                    <div className='text-4xl font-bold text-[var(--color-status-success)]'>
                      {formatUsd(actualCostPerMonth)}
                    </div>
                    <p className='mt-2 text-sm text-[var(--color-text-secondary)]'>per month</p>
                    <div className='mt-2 flex justify-center'><CostSourceBadge source='actual' window='Last 30 days' /></div>
                  </>
                ) : (
                  <NotCollected reason='No Azure billing (dbo.azure_cost_daily) for this storage account in the last 30 days' />
                )}
              </div>
            </Card>
          )}

          {/* Containers Card */}
          <Card padding='lg'>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-4'>Containers</h3>
            <div className='text-center'>
              <div className='text-4xl font-bold text-purple-600 dark:text-purple-400'>
                {account.containerCount != null
                  ? account.containerCount.toLocaleString()
                  : '—'}
              </div>
              <p className='mt-2 text-sm text-[var(--color-text-secondary)]'>discovered containers</p>
              <button
                onClick={() => setActiveTab('containers')}
                className='mt-4 text-sm text-[var(--color-accent)] hover:underline font-medium'
              >
                View containers →
              </button>
            </div>
          </Card>

          {/* Last Collected Card */}
          <Card padding='lg'>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-4'>Collection</h3>
            <div className='text-center'>
              <div className='text-sm font-medium text-[var(--color-text-primary)]'>
                {account.lastCollected
                  ? formatEastern(account.lastCollected)
                  : 'Never collected'}
              </div>
              <p className='mt-2 text-xs text-[var(--color-text-secondary)]'>last ARM snapshot</p>
              {account.noAccessSince && (
                <p className='mt-3 text-left text-xs text-[var(--color-text-secondary)]'>
                  The collection worker has not been able to see this account since {formatEasternDate(account.noAccessSince)}.
                  What is shown is how it was when last seen. Worker access in the Fabric Control portal shows how to give
                  the worker access.
                </p>
              )}
            </div>
          </Card>

          {/* Account Details */}
          <Card padding='lg' className='lg:col-span-2'>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-4'>Account Details</h3>
            <DescriptionList
              columns={2}
              items={[
                { label: 'Account Name', value: <span className='font-mono'>{account.accountName}</span> },
                {
                  label: 'Replication Type',
                  value: (
                    <>
                      {account.replicationType ?? '—'}
                      {geo && ' 🌐'}
                    </>
                  ),
                },
                { label: 'Access Tier', value: account.accessTier ?? '—' },
                { label: 'Location', value: account.location ?? '—' },
                { label: 'Resource Group', value: account.resourceGroup ?? '—' },
                { label: 'Subscription', value: account.subscriptionName },
                { label: 'Kind', value: account.kind ?? '—' },
                { label: 'Storage Tier', value: account.storageTier ?? '—' },
                { label: 'Purpose', value: account.storagePurpose ?? '—' },
                {
                  label: 'Geo-Redundant',
                  value: account.isGeoRedundant == null ? '—' : account.isGeoRedundant ? 'Yes 🌐' : 'No',
                },
                {
                  label: 'Discovered',
                  value: account.createdAt ? formatEasternDate(account.createdAt) : '—',
                },
                ...(canSeeBackup
                  ? [{
                      label: 'Protection',
                      value: <Badge variant={account.isBackupBound ? 'success' : 'secondary'} isOutlined size="sm">{account.isBackupBound ? 'Backup-bound' : 'Fleet'}</Badge>,
                    }]
                  : []),
                {
                  label: 'Public Network Access',
                  value: account.networkDefaultAction ?? <NotCollected reason="No network_default_action collected for this account" />,
                },
                {
                  label: 'Endpoint',
                  value: account.endpointUrl ? <span className="font-mono text-xs break-all">{account.endpointUrl}</span> : '—',
                },
                ...(account.tags && Object.keys(account.tags).length > 0
                  ? [
                      {
                        label: 'Tags',
                        value: (
                          <div className='flex flex-wrap gap-1'>
                            {Object.entries(account.tags).map(([k, v]) => (
                              <span
                                key={k}
                                className='px-2 py-0.5 text-xs rounded bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                              >
                                {k}: {String(v)}
                              </span>
                            ))}
                          </div>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Card>

          {/* Growth sparkline (Spec 217 FR-004) */}
          <Card padding='lg' className='lg:col-span-2'>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-4'>Growth — 60 days</h3>
            {account.growthTrend.length > 1 ? (
              <div className='h-48'>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart data={account.growthTrend}>
                    <XAxis dataKey='date' tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={40} />
                    <Tooltip {...chartTooltipStyle} />
                    <Line type='monotone' dataKey='usedGb' name='Used (GB)' stroke='var(--color-accent)' strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <NotCollected reason='Fewer than 2 days of storage account snapshots collected in the 60-day window' />
            )}
          </Card>

          {/* Azure Resource ID */}
          <Card padding='lg' className='lg:col-span-2'>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-4'>Azure Resource ID</h3>
            <div className='bg-[var(--color-bg-subtle)] rounded-lg p-4 font-mono text-xs break-all text-[var(--color-text-secondary)]'>
              {account.id}
            </div>
          </Card>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Containers Tab                                                    */}
      {/* ---------------------------------------------------------------- */}
      {activeTab === 'containers' && (
        <Card padding='lg'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>
              Containers
              {containers.length > 0 && (
                <span className='ml-2 text-sm text-[var(--color-text-secondary)]'>({containers.length})</span>
              )}
            </h3>
          </div>

          {containersError && (
            <div className='mb-4'>
              <ErrorState title='Failed to load containers' message={containersError} onRetry={fetchContainers} />
            </div>
          )}

          {containersLoading ? (
            <LoadingState rows={6} />
          ) : containers.length === 0 ? (
            <EmptyState
              icon={<Database className='h-12 w-12' />}
              title='No containers found'
              message='Containers are collected by the daily ARM collection job'
            />
          ) : (
            <>
            <QuickGlance>
              <Card className='p-4 text-sm text-[var(--color-text-secondary)]'>
                Open on a larger screen for the full containers table.
              </Card>
            </QuickGlance>
            <Extended>
            <DualScrollTable>
              <table className='min-w-full divide-y divide-[var(--color-border)]'>
                <thead className='bg-[var(--color-bg-subtle)]'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                      Container Name
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                      Public Access
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                      Status
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                      Last Modified
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
                  {pageItems.map((container, idx) => (
                    <tr key={idx} className='hover:bg-[var(--color-bg-subtle)]'>
                      <td className='px-6 py-4 text-sm font-mono text-[var(--color-text-primary)]'>
                        {container.containerName}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm'>
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            container.publicAccess === 'None' || container.publicAccess === null
                              ? 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                              : container.publicAccess === 'Blob'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                          }`}
                        >
                          {container.publicAccess ?? 'None'}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm'>
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            container.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                          }`}
                        >
                          {container.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)]'>
                        {container.lastModified
                          ? formatEastern(container.lastModified)
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DualScrollTable>
            </Extended>
            </>
          )}
          <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
        </Card>
      )}
    </DetailLayout>
  );
};

export default StorageAccountDetailPage;
